import { useEffect, useState } from 'react';
import { api } from './api';

interface SystemStatus {
  jira: { configured: boolean; host?: string };
}

let cachedHost: string | null | undefined = undefined;
let inflight: Promise<string | null> | null = null;
const subscribers = new Set<(host: string | null) => void>();

/**
 * Fetches the configured Jira host from `/api/system/status` once per page load
 * and caches the result. Subsequent callers share the same promise (or the
 * resolved value) so the rail, top bar, and Phase 1 viewer don't all hit the
 * endpoint independently.
 */
async function fetchHost(): Promise<string | null> {
  if (cachedHost !== undefined) return cachedHost;
  if (inflight) return inflight;
  inflight = api
    .get<SystemStatus>('/api/system/status')
    .then((s) => {
      cachedHost = s.jira?.configured && s.jira.host ? s.jira.host : null;
      return cachedHost;
    })
    .catch(() => {
      cachedHost = null;
      return null;
    })
    .finally(() => {
      inflight = null;
      subscribers.forEach((fn) => fn(cachedHost ?? null));
    });
  return inflight;
}

/**
 * React hook returning the Jira host (e.g. `https://leanco.atlassian.net`) or
 * null if Jira isn't configured / hasn't loaded yet. Components rendering Jira
 * keys should fall back to plain text when null.
 */
export function useJiraHost(): string | null {
  const [host, setHost] = useState<string | null>(cachedHost ?? null);
  useEffect(() => {
    if (cachedHost !== undefined) {
      setHost(cachedHost ?? null);
      return;
    }
    subscribers.add(setHost);
    void fetchHost();
    return () => {
      subscribers.delete(setHost);
    };
  }, []);
  return host;
}

export function jiraIssueUrl(host: string | null, key: string): string | null {
  if (!host) return null;
  return `${host.replace(/\/$/, '')}/browse/${encodeURIComponent(key)}`;
}
